import { useId, useState } from "react"
import { AlertCircle, AlertTriangle, Check, Loader2, Pencil, Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SourcePill } from "@/components/schedule/ProposalPopover"
import { NEEDS_VALUE_HINT, effectiveFillValue, needsUserValue } from "@/lib/fill-source"
import {
  applyErrorMessage,
  checkboxLabel,
  isCheckboxField,
  longAnswerWarning,
} from "@/lib/word-form"
import type { FillDecision, FillProposal } from "@/types/schedule"
import { cn } from "@/lib/utils"
import type { FormField } from "@/types/word-form"

// ===================
// Helpers
// ===================

/** A value as the person reads it: ticks in words, everything else verbatim. */
function displayValue(field: FormField, value: string): string {
  return isCheckboxField(field) ? checkboxLabel(value) : value
}

// ===================
// Value editor
// ===================

interface ValueEditorProps {
  field: FormField
  value: string
  onChange: (value: string) => void
}

/** One editor per field kind: a tick for checkboxes, the listed options for dropdowns. */
function ValueEditor({ field, value, onChange }: ValueEditorProps) {
  const inputId = useId()

  if (isCheckboxField(field)) {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={inputId}
          checked={value === "true"}
          onCheckedChange={(checked) => onChange(checked === true ? "true" : "false")}
        />
        <Label htmlFor={inputId} className="text-sm font-normal">
          Ticked
        </Label>
      </div>
    )
  }

  if (field.kind === "dropdown" && field.options) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 bg-muted" aria-label={`Answer for ${field.label}`}>
          <SelectValue placeholder="Choose an option" />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (field.kind === "multiline") {
    return (
      <Textarea
        aria-label={`Answer for ${field.label}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        className="bg-muted text-sm"
        autoFocus
      />
    )
  }

  return (
    <Input
      aria-label={`Answer for ${field.label}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 bg-muted text-sm"
      autoFocus
    />
  )
}

// ===================
// Field card
// ===================

export interface FormFieldCardProps {
  field: FormField
  /** The live proposal for this field, if the AI (or you) staged one. */
  proposal: FillProposal | null
  onDecide: (decision: FillDecision, editedValue?: string) => void
  /**
   * Save an answer typed into a field with no live proposal. Resolves null
   * when saved, otherwise the plain reason it was refused. Omit to hide
   * "Add answer" (read-only viewers).
   */
  onSaveManual?: (draft: string) => Promise<string | null>
  /** No decisions or typing: a viewer, or a member not on the bid. */
  readOnly?: boolean
}

/**
 * One fillable field: its label, the buyer's instruction, what the file holds
 * now and the proposed answer. Decisions are all ghost — they repeat down the
 * page, and the page's one leading action is "Fill with AI".
 */
export function FormFieldCard({
  field,
  proposal,
  onDecide,
  onSaveManual,
  readOnly = false,
}: FormFieldCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const value = proposal ? effectiveFillValue(proposal) : ""
  const isPending = proposal?.status === "pending"
  const isAccepted = proposal?.status === "accepted"
  const isApplied = isAccepted && proposal?.appliedAt !== null
  const isUnanswered = proposal ? needsUserValue(proposal) : false
  const current = field.currentValue?.trim() ? field.currentValue : null
  const draftLongWarning = longAnswerWarning(isEditing ? draft : value, field.cellWidthTwips)
  // The server's warning (stage_form_fills) already carries its own size note
  // ("Long for its cell: about …") beside a lapsed date or the AI's caution.
  // Show the server's words; our estimate only while the value is being edited.
  const serverWarnsOnSize = /\bLong\b/.test(proposal?.warning ?? "")
  const longWarning = isEditing || !serverWarnsOnSize ? draftLongWarning : null

  /** No live proposal: what is typed becomes a manual answer. */
  const isManualEntry = !proposal || proposal.status === "superseded" || proposal.status === "dismissed"

  const startEditing = () => {
    const initial = isManualEntry ? "" : effectiveFillValue(proposal)
    // A tick box always has a state: start from what the file holds now.
    setDraft(isCheckboxField(field) && !initial ? (field.currentValue === "true" ? "true" : "false") : initial)
    setSaveError(null)
    setIsEditing(true)
  }

  const cancel = () => {
    setSaveError(null)
    setIsEditing(false)
  }

  const save = async () => {
    if (!isManualEntry) {
      onDecide("accepted", draft)
      setIsEditing(false)
      return
    }
    if (!onSaveManual) return
    setIsSaving(true)
    const refusal = await onSaveManual(draft)
    setIsSaving(false)
    if (refusal) {
      setSaveError(refusal)
      return
    }
    setSaveError(null)
    setIsEditing(false)
  }

  return (
    <div data-testid="form-field" data-field-id={field.fieldId} className="space-y-2 py-4">
      <div className="space-y-0.5">
        <h3 className="text-sm font-medium">{field.label}</h3>
        {field.instruction && <p className="text-meta">{field.instruction}</p>}
        {current && (
          <p className="text-meta">In the form now: {displayValue(field, current)}</p>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <ValueEditor field={field} value={draft} onChange={setDraft} />
          {longWarning && <WarningLine>{longWarning}</WarningLine>}
          {saveError && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{saveError}</span>
            </p>
          )}
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={cancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={draft.trim().length === 0 || isSaving}
              onClick={() => void save()}
            >
              {isSaving ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="mr-1.5 h-3.5 w-3.5" />
              )}
              Save
            </Button>
          </div>
        </div>
      ) : isManualEntry ? (
        <div className="flex items-center justify-between gap-2">
          <p className={cn("text-sm", proposal?.status === "dismissed" ? "text-muted-foreground" : "text-subtle")}>
            {proposal?.status === "dismissed" ? "Dismissed" : "No answer yet"}
          </p>
          {onSaveManual && !readOnly && (
            <Button variant="ghost" size="sm" onClick={startEditing}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add answer
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {isAccepted ? (
              <Badge variant="success" className="text-[10px]">
                {isApplied ? "In completed copy" : "Accepted"}
              </Badge>
            ) : (
              <SourcePill proposal={proposal} />
            )}
          </div>

          {isUnanswered ? (
            <p className="text-sm text-muted-foreground">Needs your answer</p>
          ) : (
            <p
              data-testid="field-answer"
              className="whitespace-pre-wrap rounded-lg bg-muted/40 px-3 py-2 text-sm"
            >
              {displayValue(field, value)}
            </p>
          )}

          {proposal.rationale && <p className="text-meta">{proposal.rationale}</p>}
          {proposal.warning && <WarningLine>{proposal.warning}</WarningLine>}
          {longWarning && <WarningLine>{longWarning}</WarningLine>}
          {proposal.applyError && (
            <p className="flex items-start gap-1.5 rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              {applyErrorMessage(proposal.applyError)}
            </p>
          )}

          {isPending && !readOnly && (
            <div className="flex items-center gap-1">
              {/* Wrapper carries the hint: a disabled button is not a hover target. */}
              <span title={isUnanswered ? NEEDS_VALUE_HINT : undefined}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isUnanswered}
                  onClick={() => onDecide("accepted")}
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                  Accept
                </Button>
              </span>
              <Button variant="ghost" size="sm" onClick={startEditing}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => onDecide("dismissed")}
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function WarningLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-xs text-[hsl(var(--status-warning))]">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{children}</span>
    </p>
  )
}
